import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, FilePlus2, FileEdit, Send, XCircle,
  CheckCircle2, Archive, UserPlus, UserCheck, ListChecks, Mail,
  MailX, Clock, ArrowUpCircle, Ban,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const KIND_LABELS = {
  audit: 'System',
  notification: 'Email',
  participant: 'Roster',
};

const ACTION_META = {
  // audit
  'calendar.event_created':      { icon: FilePlus2,   tone: 'text-emerald-600 bg-emerald-50', label: 'Event created' },
  'calendar.event_updated':      { icon: FileEdit,    tone: 'text-sky-600 bg-sky-50',         label: 'Event updated' },
  'calendar.event_published':    { icon: Send,        tone: 'text-emerald-600 bg-emerald-50', label: 'Event published' },
  'calendar.event_cancelled':    { icon: XCircle,     tone: 'text-rose-600 bg-rose-50',       label: 'Event cancelled' },
  'calendar.event_completed':    { icon: CheckCircle2,tone: 'text-sky-600 bg-sky-50',         label: 'Event completed' },
  'calendar.event_archived':     { icon: Archive,     tone: 'text-slate-600 bg-slate-100',    label: 'Event archived' },
  'calendar.notification_resent':{ icon: Mail,        tone: 'text-violet-600 bg-violet-50',   label: 'Notification resent' },
  'calendar.notification_custom_sent': { icon: Mail,  tone: 'text-violet-600 bg-violet-50',   label: 'Custom email sent' },
  'calendar.waitlist_position_changed': { icon: ArrowUpCircle, tone: 'text-amber-600 bg-amber-50', label: 'Waitlist reordered' },
  // participant
  'participant.registered':      { icon: UserPlus,    tone: 'text-emerald-600 bg-emerald-50', label: 'Registered' },
  'participant.rsvp':            { icon: ListChecks,  tone: 'text-sky-600 bg-sky-50',         label: 'RSVP' },
  'participant.checked_in':      { icon: UserCheck,   tone: 'text-emerald-700 bg-emerald-50', label: 'Checked in' },
  'participant.waitlisted':      { icon: Clock,       tone: 'text-amber-600 bg-amber-50',     label: 'Added to waitlist' },
  'participant.promoted':        { icon: ArrowUpCircle,tone: 'text-emerald-600 bg-emerald-50',label: 'Promoted from waitlist' },
  'participant.cancelled':       { icon: Ban,         tone: 'text-slate-600 bg-slate-100',    label: 'Cancelled' },
};

const NOTIF_META = {
  sent:      { icon: Mail,     tone: 'text-emerald-600 bg-emerald-50' },
  processing:{ icon: Mail,     tone: 'text-sky-600 bg-sky-50' },
  scheduled: { icon: Clock,    tone: 'text-slate-600 bg-slate-100' },
  failed:    { icon: MailX,    tone: 'text-rose-600 bg-rose-50' },
  skipped:   { icon: MailX,    tone: 'text-slate-500 bg-slate-100' },
  cancelled: { icon: MailX,    tone: 'text-slate-500 bg-slate-100' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const iconFor = (it) => {
  const meta = ACTION_META[it.action];
  if (meta) return meta;
  if (it.kind === 'notification') {
    const nm = NOTIF_META[it.status] || NOTIF_META.sent;
    return { icon: nm.icon, tone: nm.tone, label: it.action?.replace('email.', 'Email · ').replace(/_/g, ' ') };
  }
  return { icon: Activity, tone: 'text-slate-600 bg-slate-100', label: it.action };
};

/**
 * Phase 27.30.12 — Activity timeline for the Event Workspace.
 *
 * Unified feed of audit_logs + email deliveries + participant lifecycle
 * events. Filterable by kind (system / email / roster). Read-only.
 */
export default function ActivityPanel({ event, canManage }) {
  const eventId = event?.event_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await calendarAPI.activity(eventId, { limit: 300 });
      setItems(data.items || []);
    } catch (e) {
      if (canManage) toast.error(getErrorMessage(e, 'Failed to load activity'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, canManage]);

  useEffect(() => { load(); }, [load]);

  if (!canManage) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-slate-500">
          Activity timeline is available to event managers only.
        </CardContent>
      </Card>
    );
  }

  const filtered = kindFilter === 'all'
    ? items
    : items.filter((i) => i.kind === kindFilter);

  return (
    <Card data-testid="activity-panel">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Activity className="h-4 w-4 text-slate-500" /> Activity timeline
            </div>
            <div className="text-xs text-slate-500">
              {items.length} event{items.length === 1 ? '' : 's'} logged
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="h-9 text-xs w-40" data-testid="activity-kind-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All activity</SelectItem>
                <SelectItem value="audit">System</SelectItem>
                <SelectItem value="notification">Email</SelectItem>
                <SelectItem value="participant">Roster</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="activity-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="activity-empty">
            No activity recorded yet.
          </p>
        ) : (
          <ol className="relative border-l border-slate-200 pl-5 space-y-4" data-testid="activity-timeline">
            {filtered.map((it) => {
              const meta = iconFor(it);
              const Icon = meta.icon;
              return (
                <li key={`${it.kind}:${it.id}`} className="relative" data-testid={`activity-row-${it.kind}`}>
                  <span
                    className={`absolute -left-[30px] top-0 h-6 w-6 rounded-full flex items-center justify-center ring-2 ring-white ${meta.tone}`}
                    aria-hidden
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-slate-800">{meta.label || it.action}</span>
                    <Badge variant="outline" className="text-[10px]">{KIND_LABELS[it.kind]}</Badge>
                    {it.status && it.kind === 'notification' && (
                      <Badge variant="outline" className="text-[10px]">{it.status}</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                    <span>{fmt(it.at)}</span>
                    {it.actor && <span>· by {it.actor}</span>}
                    {it.recipient && <span>· to {it.recipient}</span>}
                    {it.subtitle && <span>· {it.subtitle}</span>}
                  </div>
                  {it.subject && (
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-2xl">
                      &ldquo;{it.subject}&rdquo;
                    </div>
                  )}
                  {it.details && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{it.details}</div>
                  )}
                  {it.failure_reason && (
                    <div className="text-[11px] text-rose-600 mt-0.5">Failure: {it.failure_reason}</div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
